import jsPDF from "jspdf";
import UIConfig from "../../constants/UIConfig";
import magicHealthLogo from "../../../assets/images/magicHealthLogo.png";

const MARGIN = 14;
const BRAND = [27, 69, 107]; // #1b456b
const BRAND_LIGHT = [237, 244, 250]; // #edf4fa
const TEXT_DARK = [40, 46, 54];
const TEXT_MUTED = [120, 130, 140];
const BORDER = [222, 232, 241];

const NA = (v) => (v === undefined || v === null || v === "" ? "N/A" : String(v));

const loadImageSafe = (src) =>
  new Promise((resolve) => {
    if (!src) {
      resolve(null);
      return;
    }
    const img = new Image();
    img.onload = () => resolve(img);
    // Never let a broken/slow logo hang PDF generation — just skip it.
    img.onerror = () => resolve(null);
    img.src = src;
  });

/**
 * Builds the Call Review feedback summary PDF using jsPDF — the same PDF
 * package already used across the app (see DownloadPDF.js / InsightPDFDownload.js).
 * Hand-drawn layout (cards, tables, badges) since no table/UI plugin is a
 * project dependency. Returns the jsPDF instance so callers can `.save()` it
 * or pull a blob/dataurl for the future "Email to Agent" integration.
 * Async because the branding logos are loaded before drawing the header.
 */
export const buildCallReviewPDF = async ({
  vonageCallId,
  magicHealthId,
  agentName,
  memberName,
  reviewDate,
  reviewerName,
  overallScore,
  maxScore,
  overallAverage,
  sections = [],
  whatWentWell,
  whatCanImprove,
  generalComments,
}) => {
  const doc = new jsPDF("p", "mm", "a4");
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const contentWidth = pageWidth - MARGIN * 2;
  const footerReserve = 14;
  let y = 0;

  const ensureSpace = (needed) => {
    if (y + needed > pageHeight - footerReserve) {
      doc.addPage();
      y = MARGIN;
    }
  };

  /* ── Header band ── */
  const drawHeader = (clientLogo, mHealthLogo) => {
    // Logo strip — client logo left, "Powered by" Magic Health right, same
    // layout/coordinates as the other PDF exports (see InsightPDFDownload.js).
    const bandTop = 16;

    if (clientLogo) {
      const cLogoH = 7;
      const cLogoW = (clientLogo.width / clientLogo.height) * cLogoH;
      doc.addImage(clientLogo, "PNG", MARGIN, 6, cLogoW, cLogoH);
    }

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8.5);
    doc.setTextColor(150);
    doc.text("Powered by", pageWidth - 50, 10.5, { align: "right" });
    if (mHealthLogo) {
      const mLogoH = 5;
      const mLogoW = (mHealthLogo.width / mHealthLogo.height) * mLogoH;
      doc.addImage(mHealthLogo, "PNG", pageWidth - MARGIN - mLogoW, 6.5, mLogoW, mLogoH);
    }

    // Slim title bar — narrowed further so the header stays out of the way
    // of the scoring content below.
    const bandH = 10;
    doc.setFillColor(...BRAND);
    doc.rect(0, bandTop, pageWidth, bandH, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(255, 255, 255);
    doc.text("Call Review Feedback Summary", MARGIN, bandTop + 6.8);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(210, 224, 236);
    doc.text(
      `Generated ${new Date().toLocaleDateString("en-US")}`,
      pageWidth - MARGIN,
      bandTop + 6.8,
      { align: "right" }
    );

    y = bandTop + bandH + 6;
  };

  /* ── Section heading with accent rule ── */
  const addSectionHeading = (text) => {
    ensureSpace(10);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11.5);
    doc.setTextColor(...BRAND);
    doc.text(text, MARGIN, y);
    y += 2;
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.4);
    doc.line(MARGIN, y, pageWidth - MARGIN, y);
    y += 5;
  };

  /* ── Two-column "Call Details" info card ── */
  const addDetailsCard = (rows) => {
    const rowH = 11;
    const cardH = Math.ceil(rows.length / 2) * rowH + 5;
    ensureSpace(cardH);

    doc.setFillColor(...BRAND_LIGHT);
    doc.roundedRect(MARGIN, y, contentWidth, cardH, 2, 2, "F");

    const colW = contentWidth / 2;
    rows.forEach(([label, value], idx) => {
      const col = idx % 2;
      const row = Math.floor(idx / 2);
      const cellX = MARGIN + col * colW + 6;
      const cellY = y + 6.5 + row * rowH;

      doc.setFont("helvetica", "bold");
      doc.setFontSize(7.5);
      doc.setTextColor(...TEXT_MUTED);
      doc.text(label.toUpperCase(), cellX, cellY);

      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.setTextColor(...TEXT_DARK);
      doc.text(NA(value), cellX, cellY + 4.5);
    });

    y += cardH + 6;
  };

  /* ── Overall score banner ── */
  const addOverallScoreBanner = () => {
    const hasAverage =
      overallAverage?.outOf5 !== undefined || overallAverage?.percentage !== undefined;
    const bannerH = hasAverage ? 16 : 12;
    ensureSpace(bannerH + 5);

    doc.setFillColor(...BRAND);
    doc.roundedRect(MARGIN, y, contentWidth, bannerH, 2, 2, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.setTextColor(255, 255, 255);
    doc.text("OVERALL SCORE", MARGIN + 6, y + 7.5);

    doc.setFontSize(13);
    doc.text(`${overallScore ?? 0} / ${maxScore ?? 0} points`, pageWidth - MARGIN - 6, y + 8, {
      align: "right",
    });

    // Grey "Average: X / 5 • Y%" line — commented out. It's just the same
    // achieved/max ratio the backend rescales to /5 and %, redundant with
    // the points already shown above.
    // if (hasAverage) {
    //   doc.setFont("helvetica", "normal");
    //   doc.setFontSize(8);
    //   doc.setTextColor(210, 224, 236);
    //   const avgParts = [];
    //   if (overallAverage?.outOf5 !== undefined) avgParts.push(`Average: ${overallAverage.outOf5} / 5`);
    //   if (overallAverage?.percentage !== undefined) avgParts.push(`${overallAverage.percentage}%`);
    //   doc.text(avgParts.join("  •  "), pageWidth - MARGIN - 6, y + 13, { align: "right" });
    // }

    y += bannerH + 5;
  };

  /* ── Per-section criteria table ── */
  const addSectionTable = (section) => {
    const earned = (section.values || []).reduce((s, v) => s + Number(v || 0), 0);
    const fields = section.fields || [];

    const hasSummary =
      section.summary?.outOf5 !== undefined || section.summary?.percentage !== undefined;
    const headerH = hasSummary ? 9.5 : 7.5;

    ensureSpace(headerH + 4);
    doc.setFillColor(...BRAND_LIGHT);
    doc.rect(MARGIN, y, contentWidth, headerH, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND);
    doc.text(section.title, MARGIN + 3, y + 5.2);
    doc.text(`${earned} / ${section.sectionTotal} pts`, pageWidth - MARGIN - 3, y + 5.2, {
      align: "right",
    });

    // Grey "X / 5 • Y%" summary line — commented out. It's the same
    // achieved/max ratio the backend rescales to /5 and %, redundant with
    // the "earned / sectionTotal pts" already shown above.
    // if (hasSummary) {
    //   doc.setFont("helvetica", "normal");
    //   doc.setFontSize(7.5);
    //   doc.setTextColor(...TEXT_MUTED);
    //   const summaryParts = [];
    //   if (section.summary?.outOf5 !== undefined) summaryParts.push(`${section.summary.outOf5} / 5`);
    //   if (section.summary?.percentage !== undefined) summaryParts.push(`${section.summary.percentage}%`);
    //   doc.text(summaryParts.join("  •  "), pageWidth - MARGIN - 3, y + 8.7, { align: "right" });
    // }

    y += headerH;

    const labelColWidth = contentWidth - 18;

    fields.forEach((field, idx) => {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(9);
      const lines = doc.splitTextToSize(field.label, labelColWidth - 6);
      const rowH = Math.max(7, lines.length * 4.3 + 2.5);

      ensureSpace(rowH);

      if (idx % 2 === 1) {
        doc.setFillColor(249, 251, 253);
        doc.rect(MARGIN, y, contentWidth, rowH, "F");
      }

      doc.setTextColor(...TEXT_DARK);
      lines.forEach((line, lineIdx) => {
        doc.text(line, MARGIN + 3, y + 4.6 + lineIdx * 4.3);
      });

      doc.setFont("helvetica", "bold");
      doc.setTextColor(...BRAND);
      doc.text(String(section.values?.[idx] ?? 0), pageWidth - MARGIN - 3, y + 4.6, {
        align: "right",
      });

      doc.setDrawColor(...BORDER);
      doc.setLineWidth(0.2);
      doc.line(MARGIN, y + rowH, pageWidth - MARGIN, y + rowH);

      y += rowH;
    });

    y += 5;
  };

  /* ── Feedback text card with colored accent bar ── */
  const addFeedbackCard = (label, text) => {
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    const bodyText = text && text.trim() ? text.trim() : "No comments provided.";
    const lines = doc.splitTextToSize(bodyText, contentWidth - 14);
    const cardH = Math.max(16, lines.length * 4.6 + 12);

    ensureSpace(cardH + 6);

    doc.setFillColor(249, 251, 253);
    doc.roundedRect(MARGIN, y, contentWidth, cardH, 2, 2, "F");
    doc.setFillColor(...BRAND);
    doc.rect(MARGIN, y, 1.6, cardH, "F");

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9.5);
    doc.setTextColor(...BRAND);
    doc.text(label, MARGIN + 7, y + 7);

    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(...TEXT_DARK);
    lines.forEach((line, idx) => {
      doc.text(line, MARGIN + 7, y + 13.5 + idx * 4.6);
    });

    y += cardH + 6;
  };

  /* ── Build the document ── */
  // Logos are optional — failures resolve to null and the header just skips them.
  const [clientLogo, mHealthLogo] = await Promise.all([
    loadImageSafe(UIConfig?.branding?.logo),
    loadImageSafe(magicHealthLogo),
  ]);
  drawHeader(clientLogo, mHealthLogo);

  addSectionHeading("Call Details");
  addDetailsCard([
    ["Vonage Call ID", vonageCallId],
    ["Magic Health ID", magicHealthId],
    ["Agent Name", agentName],
    ["Member Name", memberName],
    ["Review Date", reviewDate],
    ["Reviewer Name", reviewerName],
  ]);

  addSectionHeading("Scores");
  const scoresStartPage = doc.internal.getCurrentPageInfo().pageNumber;
  addOverallScoreBanner();
  sections.forEach(addSectionTable);

  // Keep "Feedback" off the scoring page(s) so it reads as its own section.
  // Only force a fresh page when the scoring content actually fit on one —
  // if it already spilled onto a later page, let Feedback continue there
  // rather than leaving a blank page behind it.
  if (doc.internal.getCurrentPageInfo().pageNumber === scoresStartPage) {
    doc.addPage();
    y = MARGIN;
  }

  addSectionHeading("Feedback");
  addFeedbackCard("What Went Well", whatWentWell);
  addFeedbackCard("What Can Improve", whatCanImprove);
  addFeedbackCard("General Comments", generalComments);

  /* ── Footer on every page ── */
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(MARGIN, pageHeight - 10, pageWidth - MARGIN, pageHeight - 10);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...TEXT_MUTED);
    doc.text("Magic Health — Call Review", MARGIN, pageHeight - 5);
    doc.text(`Page ${i} of ${pageCount}`, pageWidth - MARGIN, pageHeight - 5, { align: "right" });
  }

  return doc;
};

export const downloadCallReviewPDF = async (data) => {
  const doc = await buildCallReviewPDF(data);
  const safeId = (data.magicHealthId || data.callId || "review")
    .toString()
    .replace(/[^a-z0-9-_]/gi, "_");
  doc.save(`Call_Review_Feedback_${safeId}.pdf`);
  return doc;
};

export default buildCallReviewPDF;
